import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { ConfirmDailogueComponent } from 'src/app/shared/components/dailogue/confirm-dailogue.component';
import { Api } from 'src/app/shared/constants/api';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { Message } from 'src/app/shared/constants/message';
import * as _ from 'underscore';

@Component({
    selector: 'app-service-form',
    templateUrl: './service-form.component.html',
    styleUrls: ['./service-form.component.less'],
    standalone: false
})
export class ServiceFormComponent implements OnInit {

  serviceForm: FormGroup;
  isEditedData = false;

  addPermission = false;
  editPermission = false;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private service: DataService,
    private utilityService: UtilityService,
    private permissionService: PermissionService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ServiceFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.checkAllPermission();
    this.initializeForm();
    this.patchValue();
  }

  private initializeForm(): void {
    this.serviceForm = this.fb.group({
      id: [],
      serviceCode: [
        null,
        [Validators.required, Validators.maxLength(10), Validators.pattern(/^[a-zA-Z0-9]+$/)]
      ],
      serviceDescription: [
        null,
        [Validators.required, Validators.maxLength(100), Validators.pattern(/^[a-zA-Z0-9 ]+$/)]
      ]
    });
  }

  private patchValue(): void {
    if (this.data?.id) {
      this.serviceForm.patchValue(this.data);
    }
  }

  checkDuplicate(apiParam: string, controlName: string): void {
    const control = this.serviceForm.get(controlName);
    const value = control?.value?.trim();

    if (!value || control?.hasError('required') || control?.hasError('maxlength') || control?.hasError('pattern')) {
      return;
    }

    // enableLoader='' is falsy → interceptor condition (enableLoader == null || enableLoader) is false
    // → addAPI() is NOT called → no global loader overlay during blur check
    const params = new HttpParams().set(apiParam, value).set('enableLoader', '');

    this.http.get<any>(Api.SERVICE_MASTER, { params }).subscribe((res: any) => {
      const isDuplicate = res?.contents?.some((item: any) => item.id !== this.id);
      if (isDuplicate) {
        control?.setErrors({ duplicate: true });
        const label = controlName === 'serviceCode' ? 'Service Code' : 'Service Description';
        this.utilityService.notifier(`${label} already exists`, 'warning');
      } else if (control?.hasError('duplicate')) {
        control?.setErrors(null);
      }
    });
  }

  onSave(): void {
    this.serviceForm.markAllAsTouched();
    if (this.serviceForm.valid) {
      this.isEditedData = true;
      this.openDialog(this.id ? Message.SAVE_OPEN_DAILOG_IN_EDIT : Message.SAVE_OPEN_DAILOG);
    }
  }

  private save(): void {
    const method = this.id ? 'put' : 'post';
    const message = this.id ? 'updated' : 'saved';

    this.service[method](Api.SERVICE_MASTER, this.serviceForm.value).subscribe(res => {
      if (res) {
        this.utilityService.notifier(`Service ${message} successfully`, 'success');
        this.dialogRef.close(res);
      }
    });
  }

  cancel(): void {
    if (this.serviceForm.dirty) {
      this.isEditedData = false;
      this.openDialog(this.id ? Message.CANCEL_OPEN_DAILOG_IN_EDIT : Message.CANCEL_OPEN_DAILOG);
    } else {
      this.dialogRef.close();
    }
  }

  private openDialog(message: string): void {
    const ref = this.dialog.open(ConfirmDailogueComponent, {
      data: message,
      maxWidth: '300px'
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.isEditedData ? this.save() : this.dialogRef.close();
      }
    });
  }

  private checkAllPermission(): void {
    const permissions = this.permissionService.checkPermissions(
      [
        PermissionKey.SERVICE_MODULE_ADD_PERMISSION,
        PermissionKey.SERVICE_MODULE_EDIT_PERMISSION
      ],
      PermissionKey.SERVICE_MODULE
    );

    this.addPermission = _.findWhere(permissions, { permissionCode: PermissionKey.SERVICE_MODULE_ADD_PERMISSION })?.show;
    this.editPermission = _.findWhere(permissions, { permissionCode: PermissionKey.SERVICE_MODULE_EDIT_PERMISSION })?.show;
  }

  get id() { return this.serviceForm.get('id')?.value; }
}
